// frontend/src/pages/PublicProfile.js
// Public user profile page — no auth required

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Avatar,
  Button,
  Stack,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import axios from '../api/axiosConfig';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfileTabBar from '../components/profile/ProfileTabBar';
import ContentGrid from '../components/profile/ContentGrid';
import PinnedSessionsGrid from '../components/profile/PinnedSessionsGrid';
import ProfileEmptyState from '../components/profile/ProfileEmptyState';
import ProfileFooter from '../components/profile/ProfileFooter';
import ProfileLoadingSkeleton from '../components/profile/ProfileLoadingSkeleton';

const PublicProfile = () => {
  const theme = useTheme();
  const { username } = useParams();
  const { currentUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [starred, setStarred] = useState([]);
  const [stats, setStats] = useState(null);
  const [pinnedSessions, setPinnedSessions] = useState([]);
  const [activeTab, setActiveTab] = useState('sessions');
  // Two loading states: initialLoading = full-page skeleton, contentLoading = content-area only
  const [initialLoading, setInitialLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [isPrivateProfile, setIsPrivateProfile] = useState(false);

  // Per-tab pagination state
  const [sessionsPagination, setSessionsPagination] = useState(null);
  const [projectsPagination, setProjectsPagination] = useState(null);
  const [starredPagination, setStarredPagination] = useState(null);

  // Track which tab was last fetched so we can auto-fetch when effectiveTab changes
  const lastFetchedTab = useRef(null);

  const fetchProfile = useCallback(async (tab, page = 1, append = false) => {
    try {
      // Only show full-page skeleton on very first load (no profile data yet)
      if (!append && !profile) setInitialLoading(true);
      else if (!append) setContentLoading(true);
      else setLoadingMore(true);

      const response = await axios.get(`/api/u/${username}`, {
        params: { tab, page, limit: 12 }
      });
      const data = response.data;

      if (data.privateProfile) {
        setIsPrivateProfile(true);
        setProfile(data.profile);
        return;
      }

      setProfile(data.profile);
      setStats(data.stats);
      setPinnedSessions(data.pinnedSessions || []);
      lastFetchedTab.current = tab;

      // Store pagination per-tab
      if (tab === 'sessions') {
        setSessionsPagination(data.pagination);
        setSessions(prev => append ? [...prev, ...data.sessions] : data.sessions);
      } else if (tab === 'projects') {
        setProjectsPagination(data.pagination);
        setProjects(prev => append ? [...prev, ...data.projects] : data.projects);
      } else if (tab === 'starred') {
        setStarredPagination(data.pagination);
        setStarred(prev => append ? [...prev, ...data.starred] : (data.starred || []));
      }
    } catch (err) {
      setError(err.response?.status === 404 ? 'not_found' : 'error');
    } finally {
      setInitialLoading(false);
      setContentLoading(false);
      setLoadingMore(false);
    }
  }, [username, profile]);

  // Initial fetch
  useEffect(() => {
    if (username) fetchProfile('sessions', 1);
  }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine which tab to show and whether tab bar is needed
  const hasSessions = stats ? stats.totalSessions > 0 : sessions.length > 0;
  const hasProjects = stats ? stats.totalProjects > 0 : projects.length > 0;
  const hasStarred = stats ? (stats.totalStarred || 0) > 0 : starred.length > 0;
  const nonZeroCount = [hasSessions, hasProjects, hasStarred].filter(Boolean).length;
  const showTabBar = nonZeroCount >= 2;

  // If only one type exists, force that tab
  const effectiveTab = showTabBar
    ? activeTab
    : hasSessions
      ? 'sessions'
      : hasProjects
        ? 'projects'
        : hasStarred
          ? 'starred'
          : 'sessions';

  // Auto-fetch when effectiveTab resolves to a tab we haven't fetched yet
  // (e.g. user has only projects — initial fetch was sessions, need to fetch projects)
  useEffect(() => {
    if (
      stats &&
      !initialLoading &&
      !contentLoading &&
      effectiveTab !== lastFetchedTab.current
    ) {
      lastFetchedTab.current = effectiveTab;
      setActiveTab(effectiveTab);
      fetchProfile(effectiveTab, 1);
    }
  }, [effectiveTab, stats, initialLoading, contentLoading, fetchProfile]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Scroll to top on tab switch
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // If we already have data for this tab, just switch — no re-fetch needed
    const hasData = newTab === 'sessions'
      ? sessions.length > 0
      : newTab === 'projects'
        ? projects.length > 0
        : starred.length > 0;
    if (hasData) return;

    fetchProfile(newTab, 1);
  };

  const handleLoadMore = () => {
    const pag = effectiveTab === 'sessions'
      ? sessionsPagination
      : effectiveTab === 'projects'
        ? projectsPagination
        : starredPagination;
    if (!pag || !pag.hasMore || loadingMore) return;
    fetchProfile(effectiveTab, pag.page + 1, true);
  };

  // Derive current tab's data
  const currentItems = effectiveTab === 'sessions'
    ? sessions
    : effectiveTab === 'projects'
      ? projects
      : starred;
  const currentPagination = effectiveTab === 'sessions'
    ? sessionsPagination
    : effectiveTab === 'projects'
      ? projectsPagination
      : starredPagination;
  const currentTotal = currentPagination?.total || 0;
  const currentHasMore = currentPagination?.hasMore || false;

  // Full-page skeleton — only on very first load
  if (initialLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 4, md: 8 }, mb: 6 }}>
        <ProfileLoadingSkeleton />
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 4, md: 8 }, mb: 6 }}>
        <Card sx={{ overflow: 'hidden' }}>
          <Box sx={{ height: 6, background: `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.dark})` }} />
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ErrorIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {error === 'not_found' ? 'Profile Not Found' : 'Something Went Wrong'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {error === 'not_found'
                ? `The user @${username} doesn't exist on Blackmount.`
                : 'Failed to load this profile. Please try again.'}
            </Typography>
            <Button variant="outlined" component={Link} to="/">Go to Blackmount</Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Private profile
  if (isPrivateProfile && profile) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 4, md: 8 }, mb: 6 }}>
        <Card sx={{ overflow: 'hidden' }}>
          <Box sx={{
            height: { xs: 80, md: 100 },
            background: `linear-gradient(135deg, ${theme.palette.grey[300]} 0%, ${theme.palette.grey[400]} 100%)`
          }} />
          <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center', mt: -6 }}>
            <Avatar
              sx={{
                width: 96,
                height: 96,
                mx: 'auto',
                mb: 2,
                fontSize: '2.2rem',
                bgcolor: 'grey.500',
                border: '4px solid',
                borderColor: 'background.paper',
                boxShadow: 2
              }}
            >
              {profile.username?.[0]?.toUpperCase()}
            </Avatar>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
              @{profile.username}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2.5 }}>
              <LockIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                This profile is private
              </Typography>
            </Stack>
            {!currentUser && (
              <Button
                variant="contained"
                component={Link}
                to="/signup"
                sx={{ textTransform: 'none', borderRadius: 3, px: 4, py: 1 }}
              >
                Join Blackmount
              </Button>
            )}
          </CardContent>
        </Card>
        <ProfileFooter currentUser={currentUser} />
      </Container>
    );
  }

  const seoTitle = profile?.displayName
    ? `${profile.displayName} (@${username}) — Blackmount`
    : `@${username} — Blackmount`;
  const seoDescription = profile?.bio
    || `Browse ${profile?.displayName || username}'s public sessions and projects on Blackmount.`;
  const seoCanonical = `https://app.blackmount.ai/u/${username}`;

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 4, md: 8 }, mb: 6 }}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={seoCanonical} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={seoCanonical} />
        <meta property="og:image" content={profile?.profilePicture || 'https://app.blackmount.ai/og-image.png'} />
        <meta property="og:site_name" content="Blackmount" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {profile?.displayName && <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "mainEntity": {
              "@type": "Person",
              "name": profile.displayName,
              "url": seoCanonical
            }
          })}
        </script>}
      </Helmet>

      {/* Profile Header */}
      <ProfileHeader profile={profile} stats={stats} currentUser={currentUser} />

      {/* Pinned Sessions */}
      <PinnedSessionsGrid pinnedSessions={pinnedSessions} />

      {/* Tab Bar — when 2+ content categories have items */}
      {showTabBar && (
        <ProfileTabBar
          activeTab={effectiveTab}
          onTabChange={handleTabChange}
          sessionCount={stats?.totalSessions || 0}
          projectCount={stats?.totalProjects || 0}
          starredCount={stats?.totalStarred || 0}
        />
      )}

      {/* Content Grid */}
      {currentItems.length > 0 ? (
        <ContentGrid
          items={currentItems}
          type={effectiveTab === 'starred' ? 'sessions' : effectiveTab}
          hasMore={currentHasMore}
          loading={loadingMore || contentLoading}
          onLoadMore={handleLoadMore}
          total={currentTotal}
          ownerUsername={profile?.username}
        />
      ) : (
        !contentLoading && <ProfileEmptyState username={profile?.username} />
      )}

      {/* Footer */}
      <ProfileFooter currentUser={currentUser} />
    </Container>
  );
};

export default PublicProfile;
